import type { QueryKey } from '@tanstack/react-query';
import { camelCase } from 'lodash';
import { queryClient } from '../queryClient';

export const LOOKUP_STALE_MS = 5 * 60_000;

export const PROFILE_STALE_MS = 5 * 60_000;

export const CLASSES_STALE_MS = 60_000;

export type LookupParams = Record<string, string | number | boolean | null | undefined>;

export const lookupQueryKey = (type: string, params: LookupParams = {}): QueryKey => ['lookup', camelCase(type), params];

export const profileQueryKey = (authUserId: string): QueryKey => ['auth', 'profile', authUserId];

const LOOKUPS_BY_TABLE = new Map<string, readonly string[]>([
  ['grade_levels', ['gradeLevels']],
  ['school_years', ['schoolYears']],
  ['grading_periods', ['schoolYears']],
  ['curriculums', ['curriculums']],
  ['programs', ['programs']],
  ['subjects', ['subjects']],
  ['accreditations', ['accreditations']],
  ['skills', ['skills']],
  ['grading_components', ['gradingComponent']],
  ['classes', ['classes', 'studentsByClass']],
  ['class_students', ['studentsByClass']],
  ['catalogs', ['catalogs']],
  ['users', ['teachers', 'parentTeachers', 'students', 'studentsByClass']],
  ['students', ['students', 'studentsByClass']],
  ['teachers', ['teachers']],
  ['parent_teachers', ['parentTeachers']],
]);

const LOOKUPS_BY_RPC = new Map<string, readonly string[]>([['sync_pt_companion_class', ['classes']]]);

const PROFILE_TABLES = new Set(['users', 'students', 'organizations']);

const ALL_LOOKUPS = 'all';

const LOOKUPS_BY_API_PATH: ReadonlyArray<readonly [RegExp, readonly string[] | typeof ALL_LOOKUPS]> = [
  [/^\/api\/admin\/users/, ['teachers', 'parentTeachers']],
  [/^\/api\/admin\/classes/, ['classes']],
  [/^\/api\/org-grading-components(\/|$)/, ['gradingComponent']],
  [/^\/api\/root\//, ALL_LOOKUPS],
  [/^\/api\/ai-build\//, ALL_LOOKUPS],
];

const ADMIN_API_PATH = /^\/api\/admin\//;

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

const REST_PATH = /\/rest\/v1\/(rpc\/)?([^/?#]+)/;

const isWrite = (method: string | undefined) => !READ_METHODS.has((method ?? 'GET').toUpperCase());

const markProfileStale = () =>
  queryClient.invalidateQueries({ queryKey: ['auth', 'profile'], refetchType: 'none' });

export const noteSupabaseWrite = (method: string | undefined, url: string) => {
  const match = isWrite(method) ? REST_PATH.exec(url) : null;

  if (!match) return;

  const [, rpc, name] = match;
  const types = (rpc ? LOOKUPS_BY_RPC : LOOKUPS_BY_TABLE).get(name) ?? [];

  for (const type of types) void queryClient.invalidateQueries({ queryKey: ['lookup', type] });

  if (!rpc && PROFILE_TABLES.has(name)) void markProfileStale();
};

export const noteApiWrite = (method: string | undefined, url: string | undefined) => {
  if (!url || !isWrite(method)) return;

  const path = new URL(url, 'http://api.invalid').pathname;
  const types = LOOKUPS_BY_API_PATH.find(([pattern]) => pattern.test(path))?.[1];

  if (types === ALL_LOOKUPS) {
    void queryClient.invalidateQueries({ queryKey: ['lookup'] });
  } else if (types) {
    for (const type of types) void queryClient.invalidateQueries({ queryKey: ['lookup', type] });
  } else if (ADMIN_API_PATH.test(path)) {
    void queryClient.invalidateQueries({ queryKey: ['lookup'], refetchType: 'none' });
  } else {
    return;
  }

  void markProfileStale();
};

const describeRequest = (input: RequestInfo | URL, init?: RequestInit) => {
  if (input instanceof Request) return { url: input.url, method: init?.method ?? input.method };

  return { url: String(input), method: init?.method };
};

export const lookupAwareFetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const response = await fetch(input, init);

  if (response.ok) {
    const { url, method } = describeRequest(input, init);

    noteSupabaseWrite(method, url);
  }

  return response;
};
